import { Badge } from '@eb/eds-badge';
import { gettext } from '@eb/i18n';
import React from 'react';
import { createBreakpoint } from 'react-use';
import { useOrganizerContext } from '../context';
import {
    DiscoveryEventsFromView,
    Organizer,
    OrganizerCollection,
    OrganizerCollections as OrganizerCollectionsInterface,
} from '../types';
import { FollowOrganizer } from './FollowOrganizer';
import './Main.scss';
import { MainSection } from './MainSection';
import { OrganizerAbout } from './OrganizerAbout';
import { OrganizerCollections } from './OrganizerCollections';
import { OrganizerEvents } from './OrganizerEvents';
import {
    TabNavigation,
    TabNavigationHeader,
    TabNavigationSection,
    TabNavLink,
    TabSection,
} from './TabNavigation';

interface MainProps {
    organizer: Organizer;
    events: DiscoveryEventsFromView;
    organizerCollections: OrganizerCollectionsInterface<OrganizerCollection>;
}
const useBreakPoint = createBreakpoint({ desktop: 792, mobile: 0 });

const StickyHeaderContent: React.FunctionComponent = () => {
    const organizer = useOrganizerContext() as Organizer;
    const [size, setSize] = React.useState('mini');
    const breakPoint = useBreakPoint(useBreakPoint);

    React.useEffect(() => {
        const dynamicSize = breakPoint === 'mobile' ? 'mini' : 'standard';
        setSize(dynamicSize);
    }, [breakPoint]);

    return (
        <div className="sticky-header__extra-content">
            <div className="sticky-header__organizer-name eds-text-bl">
                {organizer.name}
            </div>
            <FollowOrganizer organizer={organizer} size={size} />
        </div>
    );
};

export const Main: React.FunctionComponent<MainProps> = (props) => {
    const hasCollections =
        props.organizerCollections.collections &&
        props.organizerCollections.collections.length > 0;

    return (
        <section className="main eds-g-cell eds-g-cell-12-12 eds-g-cell-lg-10-12 eds-g-offset-lg-1-12 eds-g-cell--has-overflow">
            <TabNavigation breakPoints={{ desktop: 792, mobile: 0 }}>
                <TabNavigationHeader
                    stickyNode={<StickyHeaderContent />}
                    stickyContentClassNames="organizer-profile__sticky-header--content eds-g-cell eds-g-cell-12-12 eds-g-cell-lg-10-12 eds-g-offset-lg-1-12 eds-g-cell--has-overflow"
                >
                    <TabNavLink
                        id="about"
                        containerClassName="eds-show-down-sw"
                        hide="desktop"
                    >
                        {gettext('About')}
                    </TabNavLink>
                    <TabNavLink id="events">{gettext('Events')}</TabNavLink>
                    {hasCollections && (
                        <TabNavLink id="collections">
                            <span style={{ paddingRight: '4px' }}>
                                {gettext('Collections')}
                            </span>
                            <Badge text={gettext('New')} bgColor="success" />
                        </TabNavLink>
                    )}
                </TabNavigationHeader>
                <TabNavigationSection>
                    <TabSection
                        hide="desktop"
                        containerClassName="eds-show-down-sw"
                        intersectionOptions={{
                            rootMargin: '-160px 0px 0px 0px',
                        }}
                    >
                        <OrganizerAbout type="mobile" />
                    </TabSection>
                    <TabSection
                        id="events"
                        containerClassName="organizer-profile__events-tab-section"
                    >
                        <MainSection headerNode={gettext('Events')}>
                            <OrganizerEvents events={props.events} />
                        </MainSection>
                    </TabSection>
                    {hasCollections && (
                        <TabSection
                            id="collections"
                            containerClassName="organizer-profile__events-tab-section"
                        >
                            <MainSection headerNode={gettext('Collections')}>
                                <OrganizerCollections
                                    organizerCollections={
                                        props.organizerCollections
                                    }
                                />
                            </MainSection>
                        </TabSection>
                    )}
                </TabNavigationSection>
            </TabNavigation>
        </section>
    );
};
